// activityGeneralService.js
// Serviço frontend para o fluxo "Minhas Atividades" (ActivityGeneral).

import api from "./api";

/**
 * Busca atividades gerais de forma paginada com filtros opcionais.
 *
 * @param {number} pageNumber
 * @param {number} pageSize
 * @param {object} filters
 *   - activityId      {string}  – ID da atividade (substring numérico)
 *   - textId          {string}  – ID do texto (substring numérico)
 *   - activityTitle   {string}  – Título da atividade (substring textual)
 *   - textTitle       {string}  – Título do texto (substring textual)
 *   - creatorId       {string}  – ID do criador (substring numérico)
 *   - creatorName     {string}  – Nome do criador (substring textual)
 *   - startDateFrom   {string}  – Data de início mínima (ISO 8601, ex: "2024-01-01")
 *   - startDateTo     {string}  – Data de início máxima (ISO 8601)
 *   - progress        {string}  – ✅ MUDANÇA: substring de Activity.Status (era progressMin/progressMax)
 *   - onlyMine        {boolean} – Quando true, retorna apenas atividades criadas pelo usuário logado
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function fetchActivitiesGeneralPaged(pageNumber, pageSize, filters = {}) {
  // Monta params ignorando valores vazios/nulos para não poluir a query string
  const params = { pageNumber, pageSize };

  if (filters.activityId?.trim()) {
    params.activityId = filters.activityId.trim();
  }

  if (filters.textId?.trim()) {
    params.textId = filters.textId.trim();
  }

  if (filters.activityTitle?.trim()) {
    params.activityTitle = filters.activityTitle.trim();
  }

  if (filters.textTitle?.trim()) {
    params.textTitle = filters.textTitle.trim();
  }

  // ✅ NOVO: filtros por destinatários de permissão
  if (filters.permissionGroupName?.trim()) {
    params.permissionGroupName = filters.permissionGroupName.trim();
  }

  if (filters.permissionUserName?.trim()) {
    params.permissionUserName = filters.permissionUserName.trim();
  }

  if (filters.creatorId?.trim()) {
    params.creatorId = filters.creatorId.trim();
  }

  if (filters.creatorName?.trim()) {
    params.creatorName = filters.creatorName.trim();
  }

  if (filters.startDateFrom?.trim()) {
    params.startDateFrom = filters.startDateFrom.trim();
  }

  if (filters.startDateTo?.trim()) {
    params.startDateTo = filters.startDateTo.trim();
  }

  // ✅ MUDANÇA: era progressMin/progressMax; agora é substring de Activity.Status
  if (filters.progress?.trim()) {
    params.progress = filters.progress.trim();
  }

  if (filters.onlyMine === true) {
    params.onlyMine = true;
  }

  return api.get("/ActivityGeneral/paged", { params });
}

/**
 * Busca uma atividade geral pelo seu ID.
 * @param {number} id
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function getActivityGeneralById(id) {
  return api.get(`/ActivityGeneral/${id}`);
}

/**
 * Exclui uma atividade geral pelo seu ID.
 * @param {number} id
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteActivityGeneral(id) {
  return api.delete(`/ActivityGeneral/${id}`);
}

/**
 * Exclui múltiplas atividades gerais em lote.
 * @param {number[]} ids
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function deleteActivitiesGeneralBatch(ids) {
  return api.delete("/ActivityGeneral", { data: ids });
}

/**
 * Atualiza uma atividade geral existente.
 * Reutiliza o mesmo endpoint PUT /api/Activities/{id} do ActivityForm.vue.
 * @param {object} data - payload com activityId + campos editáveis
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export function updateActivityGeneral(data) {
  // ✅ MUDANÇA: aponta para /ActivityGeneral/{id} (era /Activities/{id})
  return api.put(`/ActivityGeneral/${data.activityId}`, data);
}